import { FloatingLabelInput } from "@/ui/components/FloatingLabelInput";
import type { TextInputProps } from "react-native";
import { ViewStyle } from "react-native";

export { useInputStyle } from "@/theme/styles";

export type TextFieldProps = {
  /** Optionales Label über dem Feld (wird als Floating-Label genutzt). */
  label?: string;
  /** Pflichtfeld-Indikator (roter Stern im Label). */
  required?: boolean;
  value: string;
  onChangeText: (text: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  keyboardType?: TextInputProps["keyboardType"];
  editable?: boolean;
  multiline?: boolean;
  numberOfLines?: number;
  /** EntityField-kompatibel: custom.placeholder, custom.lines */
  custom?: { placeholder?: string; lines?: number };
  style?: ViewStyle;
  onSubmitEditing?: TextInputProps["onSubmitEditing"];
  returnKeyType?: TextInputProps["returnKeyType"];
  blurOnSubmit?: TextInputProps["blurOnSubmit"];
};

/**
 * Einheitliches Text-Eingabefeld für die ganze App (FloatingLabelInput).
 * Wird von FormField, EntityForm, EditableCardField, Baum und Karten genutzt.
 * Unterstützt EntityField custom (placeholder, lines).
 */
export function TextField({
  label,
  required,
  value,
  onChangeText,
  placeholder,
  autoFocus,
  keyboardType,
  editable = true,
  multiline,
  numberOfLines,
  custom,
  style,
  onSubmitEditing,
  returnKeyType,
  blurOnSubmit,
}: TextFieldProps) {
  const place = placeholder ?? custom?.placeholder;
  const lines = numberOfLines ?? custom?.lines ?? 1;
  const multi = multiline ?? lines > 1;
  const floatingLabel = [label, place].filter(Boolean)[0] ?? "Eingabe";

  return (
    <FloatingLabelInput
      label={floatingLabel}
      required={required}
      value={value}
      onChangeText={onChangeText}
      autoFocus={autoFocus}
      keyboardType={keyboardType}
      editable={editable}
      multiline={multi}
      numberOfLines={lines}
      containerStyle={style}
      onSubmitEditing={onSubmitEditing}
      returnKeyType={returnKeyType}
      blurOnSubmit={blurOnSubmit}
    />
  );
}

export type FormFieldProps = Omit<TextFieldProps, "label"> & {
  label: string;
};

/**
 * Formular-Feld mit Label (nutzt intern TextField).
 * Gleicher Look wie überall sonst (Unterbereich, Tier, Profil, Entity-Felder).
 */
export function FormField({
  label,
  required,
  value,
  onChangeText,
  placeholder,
  autoFocus,
  keyboardType,
  editable = true,
  multiline,
  numberOfLines,
  custom,
  style,
  onSubmitEditing,
  returnKeyType,
  blurOnSubmit,
}: FormFieldProps) {
  return (
    <TextField
      label={label}
      required={required}
      value={value}
      onChangeText={onChangeText}
      placeholder={placeholder}
      autoFocus={autoFocus}
      keyboardType={keyboardType}
      editable={editable}
      multiline={multiline}
      numberOfLines={numberOfLines}
      custom={custom}
      style={style}
      onSubmitEditing={onSubmitEditing}
      returnKeyType={returnKeyType}
      blurOnSubmit={blurOnSubmit}
    />
  );
}
